import React, { useCallback, useMemo } from "react";
import { useFormikContext, type FormikProps } from "formik";
import cx from "classnames";
import {
  Label,
  FormGroup,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useUser } from "stores/user";
import {
  allowOnlyNumberKeys,
  limitNumberInputLength,
  handleNumberPaste,
} from "utils";
import type { FormikCustomProductValues } from "interfaces/product/product.types";
import type { FormikFieldType, SelectOption } from "interfaces/shared.types";

type CurrencyInputProps = {
  field: FormikFieldType;
  form: FormikProps<Record<string, string | null | SelectOption>>;
  wrapperClassName: string;
  label: React.ReactNode;
};

export const CurrencyInput = ({
  field,
  form,
  wrapperClassName,
  label,
  ...restProps
}: CurrencyInputProps) => {
  const user = useUser();
  const meta = form.getFieldMeta(field.name);
  const { setFieldValue } = useFormikContext<FormikCustomProductValues>();
  const isInvalid = useMemo(
    () => meta.touched && !!meta.error,
    [meta.error, meta.touched],
  );
  const handleBlur = useCallback(() => {
    const currentValue = field.value ? parseFloat(field.value) : 0;
    if (currentValue > 0) {
      setFieldValue(field.name, currentValue.toFixed(2));
    } else {
      setFieldValue(field.name, parseFloat("0").toFixed(2));
    }
  }, [field, setFieldValue]);

  return (
    <FormGroup className={wrapperClassName}>
      {label && (
        <Label
          className="label-small fw-bolder d-flex align-items-center justify-content-between mt-1 text-gray-700"
          for={field.name}
        >
          {label}
        </Label>
      )}
      {field && (
        <InputGroup>
          <Input
            {...field}
            invalid={isInvalid}
            onBlur={handleBlur}
            type="number"
            value={field.value}
            onKeyDown={allowOnlyNumberKeys}
            onInput={limitNumberInputLength}
            onPaste={handleNumberPaste}
            {...restProps}
          />
          <InputGroupText className="text-gray-700 bg-white border-start-0">
            {user?.currencyCode}
          </InputGroupText>
        </InputGroup>
      )}
      <FormFeedback className={cx("mt-2", { "d-flex": isInvalid })}>
        {meta.error}
      </FormFeedback>
    </FormGroup>
  );
};
